import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AddressDetails, PostcodeLookupProps } from '../types';
import trackEvent from '../../../../../helpers/gtm/eventPush';
import PostcodeLookupForm from './PostcodeLookupForm';
import PostcodeLookupSuggestions from './PostcodeLookupSuggestions';
import ManualAddressForm from './ManualAddressForm';
import '../../../../../../sass/components/address_lookup.scss';

import {
  getInputByName,
  getPostcodeBlock,
  getManualEntryBlock,
  getStoredAddress,
  removeStoredAddress,
  setManualEntryInput,
  setStoredAddress,
  setStreetNameInput,
  updateInputByName,
  validatePostcode,
} from '../helpers';

const PostcodeLookup: React.FC<PostcodeLookupProps> = (props) => {
  const {
    alsKey,
    alsUrl,
    isMobile,
    localisations,
    storedAddress,
    trackingContext,
  } = props;

  // State management
  const [addressData, setAddressData] = useState<AddressDetails | any>(null);
  const [addressSnippet, setAddressSnippet] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [manualEntryActive, setManualEntryActive] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [resetParam, setResetParam] = useState<number>(0);

  // Checking for a stored address in the session
  useEffect(() => {
    const { snippet, id, data = null } = storedAddress;
    if (snippet && id) {
      updateInputByName('address_snippet', snippet);
      updateInputByName('address_uuid', id);
      setAddressSnippet(snippet);
    }
    if (data) {
      const address = getAddressDetails(data);
      setAddressData(address);
    }
  }, [storedAddress]);

  // Checking for manual entry mode in the session
  useEffect(() => {
    const { manual } = storedAddress;
    if (manual) {
      setManualEntryActive(true);
      setManualEntryInput('1');
    }
  }, [storedAddress]);

  // Function to deconstruct the address details
  // from the ALS result. Used when displaying
  // the selected address.
  const getAddressDetails = (data: AddressDetails): Object => {
    const { line_1, line_2, line_3, post_town, postcode } = data;
    return {
      line_1,
      line_2,
      line_3,
      post_town,
      postcode,
    };
  };

  // Function to reset suggestions array
  const resetSuggestions = (): void => {
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Function to reset selected address
  const resetSelectedAddress = useCallback((): void => {
    setAddressSnippet('');
    setAddressData(null);
    updateInputByName('address_snippet', '');
    updateInputByName('address_uuid', '');
    removeStoredAddress();
  }, []);

  // Function to show the global form errors
  const showValidationErrors = useCallback((): void => {
    const block = getPostcodeBlock();
    if (block) {
      block.classList.add('validate_box');
    }
  }, []);

  // Function to hide global form errors
  const resetValidationErrors = useCallback((): void => {
    const block = getPostcodeBlock();
    if (block) {
      block.classList.remove('validate_box');
    }
  }, []);

  // Function to get address results from the given
  // postcode by calling the ALS endpoint.
  const findAddress = useCallback(
    (value): void => {
      if (!value) {
        return;
      }
      const territory = 'uk';
      const query = value.toLowerCase().replace(/[^0-9a-z]/gi, '');
      const postcode = encodeURIComponent(query);

      const results = axios
        .get(`${alsUrl}/addresses?territory=${territory}&postcode=${postcode}`)
        .then((response) => {
          setStoredAddress({ manual: false, postcode: value });
          // Build the result
          const result = response.data.data.map((item) => ({
            id: item.uuid,
            name: item.snippet,
          }));
          // Store in suggestions array
          setSuggestions(result);
          setShowSuggestions(true);
          setServiceError(null);
          if (result.length < 1) {
            setServiceError('Address not found');
          }
        })
        .catch((err) => {
          // Capture network errors and bad requests,
          // 400 error is likely a mis typed postcode
          const { message } = err;
          if (message) {
            const error = message.includes('400')
              ? 'Please enter a valid postcode'
              : message;
            setServiceError(error);
            setSuggestions([]);
            setShowSuggestions(false);
            if (message.toLowerCase() === 'network error') {
              setShowSuggestions(true);
            }
            return;
          }

          // Capture service errrors
          const { response } = err;
          const { errors } = response?.data;
          if (errors && errors.length > 0) {
            const { code } = errors[0];
            let error = 'Address lookup: ';
            switch (code) {
              case 1014:
                error += `invalid postcode (${code})`;
                break;
              case 1000:
                error += `unknown error (${code})`;
                break;
              default:
                break;
            }
            setServiceError(error);
            setSuggestions([]);
            setShowSuggestions(false);
          }
        });
    },
    [alsUrl],
  );

  // Function to handle the postcode input change
  const handlePostcodeChange = useCallback(
    (value, valid = false): void => {
      setSearchValue(value);
      // Save changes in session when no address selected
      setStoredAddress({ manual: false, postcode: valid ? value : '' });
      // Reset
      setServiceError(null);
      resetValidationErrors();
      resetSelectedAddress();
      resetSuggestions();
    },
    [resetSelectedAddress, resetValidationErrors],
  );

  // Function to handle the search button click
  const handleSearch = useCallback(
    (value, valid): void => {
      if (valid) {
        setSearchValue(value);
        findAddress(value);
      } else {
        resetSelectedAddress();
        resetSuggestions();
      }
    },
    [findAddress, resetSelectedAddress],
  );

  // Function to handle the select address button click
  const handleSelect = useCallback(
    async (result): Promise<void> => {
      const { name = null, id = null } = result || {};
      updateInputByName('address_snippet', name);
      updateInputByName('address_uuid', id);
      setAddressSnippet(name);
      resetValidationErrors();

      // Call ALS again to get the address details
      // /addresses/ADDRESS_ID?api_key=API_KEy
      const details = await axios
        .get(`${alsUrl}/addresses/${id}?api_key=${alsKey}`)
        .then((response) => {
          const { data } = response?.data;
          if (data) {
            const address = getAddressDetails(data);
            setAddressData(address);
            setStoredAddress({
              manual: false,
              postcode: searchValue,
              snippet: name,
              id: id,
              data: address,
            });
          }
        })
        .catch((err) => {
          setServiceError('Could not fetch address details');
          setStoredAddress({
            manual: false,
            postcode: searchValue,
            snippet: name,
            id: id,
          });
        });
    },
    [alsUrl, alsKey, resetValidationErrors, searchValue],
  );

  // Function to reset form
  const resetForm = useCallback((): void => {
    setResetParam(resetParam + 1);
    resetSelectedAddress();
    resetSuggestions();
    setSearchValue('');
    resetValidationErrors();
    setServiceError(null);
  }, [resetParam, resetSelectedAddress, resetValidationErrors]);

  // Function to toggle the label text
  const toggleLabel = useCallback(
    (type: string): void => {
      const label = document.querySelector<HTMLElement>(
        '.form_row_postcode .form_label__text',
      );
      if (!label) {
        return;
      }
      label.innerText =
        type === 'address'
          ? localisations.address_label
          : localisations.postcode_label;
    },
    [localisations],
  );

  // Function to toggle the value of the form label / hint
  const toggleHint = useCallback(
    (show: boolean): void => {
      const hint = document.querySelector<HTMLElement>(
        '.form_row_postcode .form_hint',
      );
      if (!hint) {
        return;
      }
      hint.innerText = show ? `(${localisations.placeholder_postcode})` : '';
    },
    [localisations],
  );

  // Function to show manual address entry form
  const showManualEntry = useCallback(
    (options = { track: false }): void => {
      setManualEntryInput('1');
      setManualEntryActive(true);
      resetForm();
      setStoredAddress({ manual: true, postcode: searchValue });
      toggleHint(false);
      toggleLabel('address');

      if (options.track) {
        // Send tracking data
        trackEvent({
          category: 'ads',
          action: `${trackingContext}_my_address_not_listed`,
          label: '',
        });
      }
    },
    [resetForm, searchValue, toggleHint, toggleLabel, trackingContext],
  );

  // Function to hide manual address entry form
  const hideManualEntry = useCallback(
    (options = { track: false }): void => {
      setManualEntryInput('0');
      setManualEntryActive(false);
      resetForm();
      toggleHint(true);
      toggleLabel('postcode');

      if (options.track) {
        // Send tracking data
        trackEvent({
          category: 'ads',
          action: `${trackingContext}_st_1_search_by_postcode`,
          label: '',
        });
      }
    },
    [resetForm, toggleHint, toggleLabel, trackingContext],
  );

  // This block exists so the component plays
  // nicely with the global form validation.
  // Only execute this on the initial load.
  useEffect(() => {
    if (!hasLoaded) {
      const form = document.querySelector<HTMLElement>('form.pl_step1');
      // Intercept the form submit
      if (form) {
        form.addEventListener('submit', (event) => {
          event.stopPropagation();
          const uuid = getInputByName('address_uuid');
          const manual = getManualEntryBlock();
          const input = getInputByName('address-postcode');
          const isInvalid =
            input && input.getAttribute('aria-invalid') === 'true';

          // Only continue if address_uuid
          // input element does exist
          if (!uuid) {
            return;
          }
          // When in manual entry mode reset the
          // errors and allow native input validation.
          if (manual) {
            resetValidationErrors();
          }
          // When not in manual entry mode and no address
          // is selected we prevent the form submit then
          // show the validatiion error.
          if (!manual && !uuid.value) {
            event.preventDefault();
            findAddress(searchValue);
            showValidationErrors();
          }
          // When the postcode is invalid on submit
          if (isInvalid) {
            event.preventDefault();
            showValidationErrors();
            resetSuggestions();
            resetSelectedAddress();
            const button = document.querySelector<HTMLElement>(
              '.button--postcode',
            );
            if (button) {
              button.click();
            }
          }
        });
      }
      setHasLoaded(true);
    }
  }, [
    findAddress,
    hasLoaded,
    manualEntryActive,
    resetSelectedAddress,
    resetValidationErrors,
    searchValue,
    showValidationErrors,
  ]);

  // Manual entry text prompt (for mobile)
  const ManualLinkPrompt = () => {
    return (
      <div className="address_postcode__action">
        <p>
          <button
            onClick={showManualEntry}
            type="button"
            className="autosuggest__manual-button"
          >
            {suggestions.length > 0
              ? "My address isn't listed"
              : 'Enter my address manually'}
          </button>
        </p>
      </div>
    );
  };

  // Display the selected address (for desktop)
  const SelectedAddress = () => {
    const data = addressData;
    return (
      <address className="address_postcode__selected">
        {data ? (
          <>
            {data.line_1 ? <span>{data.line_1}</span> : null}
            {data.line_2 ? <span>{data.line_2}</span> : null}
            {data.line_3 ? <span>{data.line_3}</span> : null}
            {data.post_town ? <span>{data.post_town}</span> : null}
            {data.postcode ? <span>{data.postcode}</span> : null}
          </>
        ) : null}
        <button
          onClick={() => {
            resetForm();
          }}
          className="button button--link"
          type="button"
        >
          Change
        </button>
      </address>
    );
  };

  return (
    <>
      {manualEntryActive ? (
        <ManualAddressForm
          backButtonType="postcode"
          hideManualEntry={hideManualEntry}
          localisations={localisations}
          storedAddress={storedAddress}
          getStoredAddress={getStoredAddress}
          setStoredAddress={setStoredAddress}
          setStreetNameInput={setStreetNameInput}
          trackingContext={trackingContext}
        />
      ) : (
        <>
          <PostcodeLookupForm
            serviceError={serviceError}
            handleSearch={handleSearch}
            handlePostcodeChange={handlePostcodeChange}
            resetParam={resetParam}
            storedAddress={storedAddress}
            trackingContext={trackingContext}
            validatePostcode={validatePostcode}
          />
          {showSuggestions ? (
            <>
              <PostcodeLookupSuggestions
                localisations={localisations}
                handleSelect={handleSelect}
                isMobile={isMobile}
                resetSuggestions={resetSuggestions}
                searchValue={searchValue}
                showManualEntry={showManualEntry}
                suggestions={suggestions}
                trackingContext={trackingContext}
              />
              {isMobile ? <ManualLinkPrompt /> : null}
            </>
          ) : addressSnippet ? (
            <SelectedAddress />
          ) : null}
        </>
      )}
    </>
  );
};

export default PostcodeLookup;
